import type { AlternativeOptions } from '@features/recipe-tile'
import type { RecipeCustomisations, RecipeQueryResponse } from '@library/api-menu-experience'
import type { Recipe, RecipeCoreId, RecipeUUID, SupportedOptionType } from '@library/menu-service'

import { useRecipes } from 'hooks/menu-recipes/useRecipes'
import { useRecipeDetailData } from 'routes/Menu/components/Detail/hooks/useRecipeDetailData'

type UseRecipeOptionsExperienceInput = {
  mainRecipe: Recipe
  shouldFetchOptions: boolean
  menuId: string
  numPortions: number
  deliveryDate: string
  collectionId?: string
  defaultOptionsType: SupportedOptionType
  basketHasRecipe: (recipeId: RecipeCoreId) => boolean
}

const hasExperienceAlternativeOptions = (customisations: RecipeCustomisations): boolean => {
  if (customisations.type === 'ingredients') {
    return customisations.slots.some((slot) => slot.values.length > 1)
  }

  if (customisations.type === 'recipes') {
    return customisations.options.length > 1
  }

  return false
}

const buildExperienceAlternativeOptions = ({
  mainRecipe,
  experienceRecipe,
  menuRecipes,
  basketHasRecipe,
}: {
  mainRecipe: Recipe
  experienceRecipe: RecipeQueryResponse
  menuRecipes: Recipe[]
  basketHasRecipe: (recipeId: RecipeCoreId) => boolean
}): AlternativeOptions => {
  const options = menuRecipes.map((recipe) => {
    const hasUnavailableIngredientSelection =
      experienceRecipe.customisations.type === 'ingredients' &&
      experienceRecipe.customisations.slots.some((slot) =>
        slot.values.some((value) => value.recipeId === recipe.id && !value.isAvailable),
      )

    const recipeCustomisationOption =
      experienceRecipe.customisations.type === 'recipes'
        ? experienceRecipe.customisations.options.find((option) => option.recipeId === recipe.id)
        : undefined

    return {
      recipe,
      recipeId: recipe.coreId,
      recipeName: recipe.name,
      isChecked: mainRecipe.id === recipe.id,
      isOutOfStock:
        (!recipe.isAvailable ||
          hasUnavailableIngredientSelection ||
          Boolean(recipeCustomisationOption && !recipeCustomisationOption.isAvailable)) &&
        !basketHasRecipe(recipe.coreId),
      surcharge: recipe.surcharge ? recipe.surcharge.pricePerPortion / 100 : null,
      surchargeLabel: recipeCustomisationOption?.surcharge?.perPortionSurcharge || null,
    }
  })

  const hasAlternativeOptions = hasExperienceAlternativeOptions(experienceRecipe.customisations)

  return {
    experienceIngredientSlots:
      hasAlternativeOptions && experienceRecipe.customisations.type === 'ingredients'
        ? experienceRecipe.customisations.slots.map((slot) => ({
            text: slot.text,
            values: slot.values.map((value) => ({
              recipeId: value.recipeId as RecipeUUID | undefined,
              name: value.name,
              isAvailable: value.isAvailable,
            })),
          }))
        : undefined,
    options: hasAlternativeOptions ? options : null,
    optionsType: hasAlternativeOptions ? experienceRecipe.customisations.type : 'none',
  }
}

const getExperienceOptionIds = (
  recipeId: RecipeUUID,
  customisations: RecipeCustomisations,
): RecipeUUID[] => {
  if (customisations.type === 'ingredients') {
    return Array.from(
      new Set([
        recipeId,
        ...customisations.slots.flatMap((slot) =>
          slot.values
            .map((value) => value.recipeId)
            .filter((optionRecipeId): optionRecipeId is RecipeUUID => Boolean(optionRecipeId)),
        ),
      ]),
    )
  }

  if (customisations.type === 'recipes') {
    return customisations.options.map((option) => option.recipeId as RecipeUUID)
  }

  return []
}

export const useRecipeOptionsExperience = ({
  mainRecipe,
  shouldFetchOptions,
  menuId,
  numPortions,
  deliveryDate,
  collectionId,
  defaultOptionsType,
  basketHasRecipe,
}: UseRecipeOptionsExperienceInput): {
  isLoadingOptions: boolean
  alternativeOptions: AlternativeOptions
} => {
  const {
    loading,
    recipe: experienceRecipeData,
    error,
  } = useRecipeDetailData({
    menuId,
    recipeUuid: mainRecipe.id,
    numPortions,
    deliveryDate,
    shouldFetchOptions,
    collectionId,
    isMenuExperienceRecipeDetailEnabled: true,
  })

  const experienceRecipe = experienceRecipeData as RecipeQueryResponse | null
  const optionIds =
    shouldFetchOptions && experienceRecipe
      ? getExperienceOptionIds(mainRecipe.id, experienceRecipe.customisations)
      : []
  const menuRecipes: Recipe[] = useRecipes(optionIds).filter(
    (recipe): recipe is Recipe => recipe !== null,
  )

  if (!shouldFetchOptions || !experienceRecipe || error) {
    return {
      isLoadingOptions: loading,
      alternativeOptions: {
        optionsType: defaultOptionsType,
        options: null,
      },
    }
  }

  return {
    isLoadingOptions: loading,
    alternativeOptions: buildExperienceAlternativeOptions({
      mainRecipe,
      experienceRecipe,
      menuRecipes,
      basketHasRecipe,
    }),
  }
}
